// Progressive reveal — the ChatGPT feel without a streaming RPC. core.md §8.6.
//
// The AI service's `AskDoctorQuestion` is unary, so the client receives the
// whole answer at once and REVEALS it in word chunks: ~35 ms per tick, with the
// chunk size chosen so that even a 1 500-word answer finishes within ~6 s. The
// composer's «توقف» calls `stop()`, which jumps to the end — the full message is
// already stored, nothing is lost by skipping the animation.
//
// Honest about latency: the caller shows «در حال فکر کردن…» until the unary
// call returns, and only then starts this. Pure and injectable — tests pass
// their own timers.

export const TICK_MS = 35;
export const MAX_REVEAL_MS = 6000;

/** Split into word tokens that keep their trailing whitespace, so joining is exact. */
export function tokenize(text) {
  const src = String(text ?? '');
  if (!src) return [];
  return src.match(/\S+\s*|\s+/g) || [];
}

/**
 * How many tokens to release per tick so the whole text lands within `maxMs`.
 * Never below 1; a short answer reveals a word at a time.
 */
export function chunkSizeFor(tokenCount, { tickMs = TICK_MS, maxMs = MAX_REVEAL_MS } = {}) {
  const ticks = Math.max(1, Math.floor(maxMs / tickMs));
  return Math.max(1, Math.ceil(tokenCount / ticks));
}

/**
 * Start revealing `text`.
 *
 * @param onUpdate  (partialText, { done }) => void — called on every tick and once
 *                  with the full text when finished or stopped
 * @param onDone    () => void
 * @returns { stop(), done }  — `stop()` finishes immediately (the full text is
 *                  delivered in one final `onUpdate`).
 */
export function startReveal(text, {
  onUpdate, onDone, tickMs = TICK_MS, maxMs = MAX_REVEAL_MS,
  setTimeout: schedule = globalThis.setTimeout, clearTimeout: cancel = globalThis.clearTimeout,
} = {}) {
  const full = String(text ?? '');
  const tokens = tokenize(full);
  const chunk = chunkSizeFor(tokens.length, { tickMs, maxMs });
  let at = 0;
  let timer = null;
  let finished = false;

  const finish = () => {
    if (finished) return;
    finished = true;
    if (timer != null) cancel(timer);
    timer = null;
    onUpdate?.(full, { done: true });
    onDone?.();
  };

  const tick = () => {
    if (finished) return;
    at = Math.min(tokens.length, at + chunk);
    const partial = tokens.slice(0, at).join('');
    if (at >= tokens.length) { finish(); return; }
    onUpdate?.(partial, { done: false });
    timer = schedule(tick, tickMs);
  };

  if (!tokens.length) { finish(); return { stop: finish, get done() { return finished; } }; }
  onUpdate?.('', { done: false });
  timer = schedule(tick, tickMs);

  return { stop: finish, get done() { return finished; } };
}

export default startReveal;
